import { useQuery } from '@tanstack/react-query';

import type { StudyPeriodState } from '@verantos-dev/app-state-cohort-definition';
import type {
  CohortVersion,
  CohortBuilderDefinitionLegacy,
  DerivedEventLegacy,
  IndexDateVariableLegacy,
  SimpleEventLegacy,
  SubjectVariableLegacy,
} from '@verantos-dev/types-cohorts';
import type { ObjectResponse } from '@verantos-dev/types-common';

import { authFetch } from './fetchers';
import { avKeys } from './queryKeys';
import type { AVUIModel } from '../adapters/analyticalVariableAdapter';
import { avToUI } from '../adapters/analyticalVariableAdapter';

export interface CohortVersionParams {
  projectId: string;
  cohortId: string;
  cohortVersionId: string;
}

/** Criteria union type derived from the cohort definition schema. */
export type Criterion = NonNullable<CohortBuilderDefinitionLegacy['criteria']>[number];

export interface CohortDefinitionQueryData {
  analyticalVariables: AVUIModel[];
  criteria: Criterion[];
  studyPeriod: StudyPeriodState;
  simpleEvents: SimpleEventLegacy[];
  derivedEvents: DerivedEventLegacy[];
  indexDate: IndexDateVariableLegacy | undefined;
  subjectVariables: SubjectVariableLegacy[];
  /** True when the returned definition is an uncommitted draft (explicit_save). */
  hasStagedChanges: boolean;
}

/**
 * Shared query function to fetch and transform cohort definition data.
 * Used by both the TanStack Query hook and the socket handler.
 *
 * @param projectId - Project ID
 * @param cohortId - Cohort ID
 * @param cohortVersionId - Cohort version ID
 * @returns Transformed cohort definition data (AVs, criteria, study period)
 */
/**
 * Pure transform from a raw cohort definition (the `jsonValue` shape returned by
 * the cohort-version endpoint, which is identical to the autosave PUT payload)
 * into the query's cached shape.
 *
 * Extracted so the autosave can seed the cache via `setQueryData` with a result
 * that is byte-identical to a real fetch — see `useEmitResearchAssistantUpdate`.
 * A single transform guarantees the optimistic cache entry and a later server
 * fetch produce the same object (notably the `avToUI` mapping on analytical
 * variables), so navigating away and back never disagrees with what was saved.
 */
export const cohortDefinitionToQueryData = (
  definition: CohortBuilderDefinitionLegacy | undefined,
  hasStagedChanges: boolean
): CohortDefinitionQueryData => {
  const studyPeriodRestrictions = definition?.studyPeriodRestrictions;
  return {
    analyticalVariables: (definition?.analyticalVariables ?? []).map(avToUI),
    criteria: definition?.criteria ?? [],
    studyPeriod: {
      overallStudyPeriodRestriction: studyPeriodRestrictions?.overallStudyPeriodRestriction,
      changeStatus: studyPeriodRestrictions?.changeStatus,
      changedBy: studyPeriodRestrictions?.changedBy,
    },
    simpleEvents: definition?.simpleEvents ?? [],
    derivedEvents: definition?.derivedEvents ?? [],
    indexDate: definition?.indexDate,
    subjectVariables: definition?.subjectVariables ?? [],
    hasStagedChanges,
  };
};

export const fetchCohortDefinition = async (
  projectId: string,
  cohortId: string,
  cohortVersionId: string
): Promise<CohortDefinitionQueryData> => {
  const response = await authFetch<ObjectResponse<CohortVersion>>(
    `/cohorts/${projectId}/cohort-profile/${cohortId}/cohort-version/${cohortVersionId}`
  );

  return cohortDefinitionToQueryData(response.data?.jsonValue, !!response.data?.hasStagedChanges);
};

/**
 * Fetch the cohort version and extract cohort definition data
 * (analytical variables, criteria, study period restrictions).
 * Uses the same endpoint as RTK Query's `getCohortVersion` but manages
 * caching independently via TanStack Query.
 */
export const useCohortDefinitionQuery = (
  params: CohortVersionParams,
  options?: { enabled?: boolean }
) => {
  const { projectId, cohortId, cohortVersionId } = params;

  return useQuery<CohortDefinitionQueryData>({
    queryKey: avKeys.list(cohortVersionId),
    queryFn: () => fetchCohortDefinition(projectId, cohortId, cohortVersionId),
    enabled: options?.enabled !== false && !!projectId && !!cohortId && !!cohortVersionId,
  });
};
